/**
 * SleepyCat Analytics Runtime (SCAnalytics)
 *
 * Declarative, data-attribute-driven event dispatcher. Reads
 * data-analytics-* attributes off DOM elements, builds a
 * GA4-shaped payload, and pushes to window.dataLayer for GTM.
 *
 * Contract (liquid authors):
 *   data-analytics-event          (required)  event name (snake_case, ≤40 chars)
 *   data-analytics-action-click   (optional)  fires on click with this section_action
 *   data-analytics-action-view    (optional)  fires on 50% impression with this section_action
 *   data-analytics-section        (recommended) logical section name
 *   data-analytics-section-id     (recommended) shopify section.id
 *   data-analytics-position       (optional)  1-based index (→ click_position; also → items[0].index for item events)
 *   data-analytics-url            (optional)  fallback link URL for non-anchor clickable elements (buttons)
 *
 *   For item-level events (select_item, view_item, add_to_cart, etc.):
 *     data-analytics-item-id, item-name, item-brand, item-category, item-variant,
 *     item-price (→ "price"), item-list-id, item-list-name
 *   → runtime auto-constructs ecommerce.items[0] envelope per GA4 spec.
 *
 *   For promotion events (view_promotion, select_promotion):
 *     data-analytics-promotion-id, promotion-name, creative-slot, creative-name
 *   → runtime nests under ecommerce.
 *
 *   Any other data-analytics-<key> becomes a top-level custom param <key>.
 *
 * Output conforms to GA4 Enhanced Measurement param names where possible:
 *   link_url, link_text, link_domain (aligns with EM's outbound_click event)
 *
 * Programmatic API:
 *   window.SCAnalytics.push(eventName, params)
 *   window.SCAnalytics.registerImpressions(rootEl?)
 *
 * @version 1.1.0
 */
(function () {
  'use strict';
  if (window.SCAnalytics) return;
  if (window.Shopify && window.Shopify.designMode) return;

  window.dataLayer = window.dataLayer || [];

  var PREFIX = 'data-analytics-';
  var CLICK_ATTR = PREFIX + 'action-click';
  var VIEW_ATTR = PREFIX + 'action-view';

  // GA4 events that require { ecommerce: null } reset before firing
  var ECOM_EVENTS = /^(view_item_list|select_item|view_item|add_to_cart|remove_from_cart|add_to_wishlist|view_cart|begin_checkout|add_shipping_info|add_payment_info|view_promotion|select_promotion|purchase|refund)$/;
  // Events that carry a single item — runtime moves item-* attrs into ecommerce.items[0]
  var ITEM_EVENTS = /^(select_item|view_item|add_to_cart|remove_from_cart|add_to_wishlist|add_shipping_info|add_payment_info)$/;
  // Events that carry promotion metadata — runtime moves promotion-* / creative-* into ecommerce
  var PROMO_EVENTS = /^(view_promotion|select_promotion)$/;

  // Keys consumed by the runtime itself — never passed through as payload params
  var RESERVED_KEYS = ['event', 'section', 'section-id', 'position', 'action-click', 'action-view', 'url'];

  // Maps data-analytics-<key> → field name inside ecommerce.items[0]
  // Missing from map = stays at event level (or ignored if not applicable)
  var ITEM_FIELD_MAP = {
    'item-id': 'item_id',
    'item-name': 'item_name',
    'item-brand': 'item_brand',
    'item-category': 'item_category',
    'item-category2': 'item_category2',
    'item-category3': 'item_category3',
    'item-variant': 'item_variant',
    'item-variant-id': 'item_variant',
    'item-price': 'price',
    'quantity': 'quantity',
    'discount': 'discount',
    'affiliation': 'affiliation'
  };

  // Numeric fields inside items[] — coerce from string to Number
  var NUMERIC_ITEM_FIELDS = { price: 1, quantity: 1, discount: 1 };
  // Numeric fields at event level — GA4 requires numbers for value/tax/shipping.
  // `currency` stays string (ISO 4217 code).
  var NUMERIC_TOP_LEVEL_FIELDS = { value: 1, tax: 1, shipping: 1 };

  function readAttrs(el) {
    var out = {};
    var attrs = el.attributes;
    for (var i = 0; i < attrs.length; i++) {
      var a = attrs[i];
      if (a.name.indexOf(PREFIX) === 0) {
        out[a.name.slice(PREFIX.length)] = a.value;
      }
    }
    return out;
  }

  function linkDomain(url) {
    if (!url) return null;
    if (url.indexOf('tel:') === 0) return 'tel';
    if (url.indexOf('mailto:') === 0) return 'mailto';
    if (/wa\.me|whatsapp/i.test(url)) return 'whatsapp';
    try {
      return new URL(url, location.href).host === location.host ? 'internal' : 'external';
    } catch (e) {
      return 'internal';
    }
  }

  function elementText(el) {
    var t = (el.textContent || el.getAttribute('aria-label') || el.getAttribute('title') || '').trim();
    return t ? t.slice(0, 100) : null;
  }

  // Extract item-* data-attrs into ecommerce.items[0]; attach event-level
  // item_list_id / item_list_name. Called for ITEM_EVENTS.
  function buildItemEnvelope(raw, clickPosition) {
    var item = {};
    var hasItem = false;
    var rawKeys = Object.keys(raw);
    for (var i = 0; i < rawKeys.length; i++) {
      var k = rawKeys[i];
      if (!ITEM_FIELD_MAP.hasOwnProperty(k)) continue;
      var destKey = ITEM_FIELD_MAP[k];
      var value = raw[k];
      if (NUMERIC_ITEM_FIELDS[destKey]) value = parseFloat(value) || 0;
      item[destKey] = value;
      hasItem = true;
    }
    if (!hasItem) return null;

    if (clickPosition != null) item.index = clickPosition;

    var listId = raw['item-list-id'] || null;
    var listName = raw['item-list-name'] || null;
    if (listId) item.item_list_id = listId;
    if (listName) item.item_list_name = listName;

    return {
      item_list_id: listId,
      item_list_name: listName,
      items: [item]
    };
  }

  // Extract promotion-* / creative-* data-attrs into ecommerce envelope.
  function buildPromoEnvelope(raw) {
    var envelope = {};
    var any = false;
    var map = {
      'promotion-id': 'promotion_id',
      'promotion-name': 'promotion_name',
      'creative-slot': 'creative_slot',
      'creative-name': 'creative_name'
    };
    var keys = Object.keys(map);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      if (raw[k]) { envelope[map[k]] = raw[k]; any = true; }
    }
    return any ? envelope : null;
  }

  // Keys consumed into ecommerce envelope — don't also pass as top-level
  var ITEM_KEYS_CONSUMED = Object.keys(ITEM_FIELD_MAP).concat(['item-list-id', 'item-list-name']);
  var PROMO_KEYS_CONSUMED = ['promotion-id', 'promotion-name', 'creative-slot', 'creative-name'];

  function push(el, action) {
    var raw = readAttrs(el);
    if (!raw.event || !action) return;

    var linkUrl = el.href || raw.url || null;
    var clickPosition = raw.position ? parseInt(raw.position, 10) : null;

    var payload = {
      event: raw.event,
      section_name: raw.section || null,
      section_action: action,
      section_id: raw['section-id'] || null,
      click_position: clickPosition,
      link_text: elementText(el),
      link_url: linkUrl,
      link_domain: linkDomain(linkUrl),
      schema_version: 'v2'
    };

    var isItemEvent = ITEM_EVENTS.test(raw.event);
    var isPromoEvent = PROMO_EVENTS.test(raw.event);

    // Determine which raw keys should be EXCLUDED from top-level pass-through
    var skipKeys = RESERVED_KEYS.slice();
    if (isItemEvent) skipKeys = skipKeys.concat(ITEM_KEYS_CONSUMED);
    if (isPromoEvent) skipKeys = skipKeys.concat(PROMO_KEYS_CONSUMED);

    // Pass-through for section-specific business fields (not consumed above).
    // Top-level numeric fields (value, tax, shipping) get coerced per GA4 spec.
    var keys = Object.keys(raw);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      if (skipKeys.indexOf(k) >= 0) continue;
      var destKey = k.replace(/-/g, '_');
      var val = raw[k];
      if (NUMERIC_TOP_LEVEL_FIELDS[destKey]) val = parseFloat(val) || 0;
      payload[destKey] = val;
    }

    // Build ecommerce envelope for item/promotion events
    var ecommerce = null;
    if (isItemEvent) ecommerce = buildItemEnvelope(raw, clickPosition);
    if (isPromoEvent) ecommerce = buildPromoEnvelope(raw);
    if (ecommerce) payload.ecommerce = ecommerce;

    // Dev-mode feedback: an item-level event with no item data will be dropped
    // silently by GA4. Warn so merchandisers notice missing data-analytics-item-* attrs.
    if (isItemEvent && !ecommerce && window.console && console.warn) {
      console.warn('[SCAnalytics] "' + raw.event + '" fired without data-analytics-item-* attrs — GA4 will drop this event.', el);
    }

    if (ECOM_EVENTS.test(raw.event)) {
      window.dataLayer.push({ ecommerce: null });
    }
    window.dataLayer.push(payload);
  }

  // Delegated click listener — bubbling phase, anchors still navigate normally
  document.addEventListener('click', function (e) {
    var el = e.target.closest('[' + CLICK_ATTR + ']');
    if (el) push(el, el.getAttribute(CLICK_ATTR));
  });

  // Impression tracking via IntersectionObserver (one-shot per element)
  var seen = new WeakSet();
  var io = null;
  if (typeof IntersectionObserver !== 'undefined') {
    io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;
        if (entry.intersectionRatio < 0.5) continue;
        if (seen.has(entry.target)) continue;
        seen.add(entry.target);
        push(entry.target, entry.target.getAttribute(VIEW_ATTR));
        io.unobserve(entry.target);
      }
    }, { threshold: 0.5 });
  }

  function registerImpressions(root) {
    if (!io) return;
    var nodes = (root || document).querySelectorAll('[' + VIEW_ATTR + ']');
    for (var i = 0; i < nodes.length; i++) {
      if (!seen.has(nodes[i])) io.observe(nodes[i]);
    }
  }

  window.SCAnalytics = {
    push: function (event, params) {
      var payload = { event: event, schema_version: 'v2' };
      if (params) {
        var keys = Object.keys(params);
        for (var i = 0; i < keys.length; i++) payload[keys[i]] = params[keys[i]];
      }
      if (ECOM_EVENTS.test(event)) {
        window.dataLayer.push({ ecommerce: null });
      }
      window.dataLayer.push(payload);
    },
    registerImpressions: registerImpressions
  };

  // Auto-register impressions on load. For dynamically injected sections
  // (AJAX, theme editor), liquid can call SCAnalytics.registerImpressions(root).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { registerImpressions(); });
  } else {
    registerImpressions();
  }
})();
